import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import {
  ArrowUpRight,
  Cpu,
  Bot,
  Palette,
  Code2,
  Lightbulb,
  FolderHeart,
  Sparkles,
  Rocket,
} from "lucide-react";
import { CALENDLY_URL } from "../lib/site";
import { MaskedLines } from "../lib/motion";

function ParallaxTile({
  className = "",
  bg,
  rotate = 0,
  delay = 0,
  parallaxY = 0,
  children,
  testid,
}) {
  return (
    <motion.div
      data-testid={testid}
      style={{
        backgroundColor: bg,
        y: parallaxY,
      }}
      initial={{ opacity: 0, scale: 0.85, rotate: rotate + 8, y: 60 }}
      animate={{ opacity: 1, scale: 1, rotate }}
      transition={{
        duration: 1.1,
        delay: 0.35 + delay,
        ease: [0.22, 1, 0.36, 1],
      }}
      className={`absolute rounded-2xl nb-border-thick nb-shadow ${className}`}
    >
      <motion.div
        className="w-full h-full p-4"
        animate={{ y: [0, -8, 0] }}
        transition={{
          duration: 6 + delay * 2,
          repeat: Infinity,
          ease: "easeInOut",
          delay: delay * 0.7,
        }}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yFar = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yMid = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const yNear = useTransform(scrollYProgress, [0, 1], [0, 20]);
  const eyebrowY = useTransform(scrollYProgress, [0, 1], [0, -30]);
  const dashboardScale = useTransform(scrollYProgress, [0, 1], [1, 0.92]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden border-b-2 border-[#1A1A1A] bg-white"
    >
      <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
      <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#FF007F]/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 -left-20 h-72 w-72 rounded-full bg-[#00C2B8]/15 blur-3xl pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-5 md:px-10 lg:px-12 pt-14 md:pt-20 pb-20 md:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left: copy */}
          <div className="lg:col-span-7">
            <motion.div
              style={{ y: eyebrowY }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 bg-white border-2 border-[#1A1A1A] rounded-full px-4 py-1.5 nb-shadow-sm"
            >
              <span className="h-2 w-2 rounded-full bg-[#FF007F] animate-spark" />
              <span className="text-xs font-bold tracking-[0.18em] uppercase">
                Education · Makerspaces · STEAM
              </span>
            </motion.div>

            <h1
              data-testid="hero-headline"
              className="mt-6 font-display font-black text-5xl sm:text-6xl lg:text-7xl xl:text-[5.25rem] leading-[0.95] tracking-tighter text-[#1A1A1A]"
            >
              <MaskedLines
                lines={["Preparing young", "people for a world"]}
              />
              <span className="block overflow-hidden pb-[0.08em]" aria-hidden>
                <motion.span
                  className="block will-change-transform"
                  initial={{ y: "110%", rotate: 4 }}
                  animate={{ y: "0%", rotate: 0 }}
                  transition={{
                    duration: 1.05,
                    ease: [0.22, 1, 0.36, 1],
                    delay: 0.11 * 2,
                  }}
                >
                  that{" "}
                  <span className="relative inline-block">
                    <span className="relative z-10 px-2">doesn’t</span>
                    <motion.span
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      transition={{
                        duration: 0.6,
                        ease: [0.22, 1, 0.36, 1],
                        delay: 1.3,
                      }}
                      style={{ transformOrigin: "left center" }}
                      className="absolute inset-x-0 bottom-1 h-3 md:h-4 bg-[#FFD700] -z-0 rounded-sm"
                    />
                  </span>{" "}
                  exist yet.
                </motion.span>
              </span>
            </h1>

            <motion.p
              data-testid="hero-subheadline"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.7 }}
              className="mt-7 text-lg md:text-xl text-[#4A4A4A] font-medium leading-relaxed max-w-2xl"
            >
              TinkerShift helps schools and libraries build future-ready
              learning ecosystems through makerspaces, creativity, STEM,
              entrepreneurship and project-based learning.
            </motion.p>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1, duration: 0.6 }}
              className="mt-4 font-mono text-xs md:text-sm text-[#1A1A1A]/70 uppercase tracking-wider"
            >
              Powered by TinkerTank — trusted by thousands of students across
              over a decade of hands-on learning.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.7 }}
              className="mt-9 flex flex-col sm:flex-row gap-4"
            >
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-book-call-btn"
                className="inline-flex items-center justify-center gap-2 bg-[#0055FF] text-white border-2 border-[#1A1A1A] rounded-full px-7 py-4 font-bold uppercase tracking-wide nb-shadow nb-press"
              >
                Book a Discovery Call{" "}
                <ArrowUpRight size={18} strokeWidth={2.5} />
              </a>
              <Link
                to="/solutions"
                data-testid="hero-explore-framework-btn"
                className="inline-flex items-center justify-center gap-2 bg-[#FFD700] text-[#1A1A1A] border-2 border-[#1A1A1A] rounded-full px-7 py-4 font-bold uppercase tracking-wide nb-shadow nb-press"
              >
                Explore Our Solutions
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.4, duration: 0.7 }}
              className="mt-12 flex flex-wrap items-center gap-4 text-sm"
            >
              <div className="inline-flex items-center gap-2 bg-white border-2 border-[#1A1A1A] rounded-full px-3 py-1 font-bold">
                <span className="h-2 w-2 rounded-full bg-[#00C2B8]" />
                15,000+ students
              </div>
              <div className="inline-flex items-center gap-2 bg-white border-2 border-[#1A1A1A] rounded-full px-3 py-1 font-bold">
                <span className="h-2 w-2 rounded-full bg-[#FF007F]" />
                10+ years delivered
              </div>
              <div className="inline-flex items-center gap-2 bg-white border-2 border-[#1A1A1A] rounded-full px-3 py-1 font-bold">
                <span className="h-2 w-2 rounded-full bg-[#FF6600]" />
                Schools · Libraries · Councils
              </div>
            </motion.div>
          </div>

          {/* Right: makerspace dashboard with parallax */}
          <div className="lg:col-span-5">
            <motion.div
              style={{ scale: dashboardScale }}
              data-testid="hero-makerspace-dashboard"
              className="relative aspect-[4/5] sm:aspect-[5/5] lg:aspect-[4/5] w-full max-w-[520px] mx-auto"
              aria-hidden
            >
              <motion.div
                initial={{ opacity: 0, y: 40, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
                className="absolute inset-0 bg-[#F8F9FA] nb-border-thick rounded-3xl nb-shadow-lg overflow-hidden"
              >
                <div className="absolute top-0 inset-x-0 h-9 bg-[#1A1A1A] flex items-center px-4 gap-2">
                  <span className="h-3 w-3 rounded-full bg-[#FF007F]" />
                  <span className="h-3 w-3 rounded-full bg-[#FFD700]" />
                  <span className="h-3 w-3 rounded-full bg-[#00C2B8]" />
                  <span className="ml-3 font-mono text-[10px] uppercase tracking-[0.25em] text-white/80">
                    tinkershift.dashboard
                  </span>
                </div>
                <div className="absolute inset-0 top-9 bg-dots opacity-60" />
              </motion.div>

              <ParallaxTile
                bg="#0055FF"
                rotate={-4}
                delay={0.1}
                parallaxY={yFar}
                className="left-[6%] top-[16%] w-[44%] aspect-[5/4] text-white"
                testid="hero-tile-audit"
              >
                <div className="flex items-center justify-between">
                  <Cpu size={28} strokeWidth={2.5} />
                  <span className="font-mono text-[10px] uppercase">Audit</span>
                </div>
                <p className="mt-6 font-display font-black text-2xl leading-tight">
                  Space audit &amp; plan
                </p>
                <div className="mt-3 flex gap-1">
                  <span className="h-1.5 w-6 rounded-full bg-white/90" />
                  <span className="h-1.5 w-3 rounded-full bg-white/60" />
                  <span className="h-1.5 w-2 rounded-full bg-white/30" />
                </div>
              </ParallaxTile>

              <ParallaxTile
                bg="#FFD700"
                rotate={3}
                delay={0.5}
                parallaxY={yMid}
                className="right-[4%] top-[10%] w-[42%] aspect-square"
                testid="hero-tile-robotics"
              >
                <div className="flex items-center justify-between">
                  <Bot size={28} strokeWidth={2.5} />
                  <span className="font-mono text-[10px] uppercase">Build</span>
                </div>
                <p className="mt-4 font-display font-black text-xl leading-tight">
                  Robotics &amp; coding
                </p>
                <div className="mt-3 grid grid-cols-4 gap-1">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <span
                      key={i}
                      className="h-2 rounded-sm"
                      style={{
                        backgroundColor: i % 3 === 0 ? "#1A1A1A" : "#1A1A1A33",
                      }}
                    />
                  ))}
                </div>
              </ParallaxTile>

              <ParallaxTile
                bg="#FF007F"
                rotate={-3}
                delay={0.7}
                parallaxY={yNear}
                className="left-[10%] top-[52%] w-[38%] aspect-square text-white"
                testid="hero-tile-creativity"
              >
                <Palette size={28} strokeWidth={2.5} />
                <p className="mt-4 font-display font-black text-xl leading-tight">
                  Creativity
                </p>
                <p className="mt-1 font-mono text-[10px] uppercase opacity-90">
                  Storytelling · Design
                </p>
              </ParallaxTile>

              <ParallaxTile
                bg="#00C2B8"
                rotate={5}
                delay={0.3}
                parallaxY={yFar}
                className="right-[8%] top-[42%] w-[34%] aspect-[5/6]"
                testid="hero-tile-coding"
              >
                <Code2 size={26} strokeWidth={2.5} />
                <p className="mt-3 font-display font-black text-lg leading-tight">
                  Code &amp; make
                </p>
                <div className="mt-2 space-y-1 font-mono text-[10px]">
                  <p>{"> if (curious)"}</p>
                  <p>{"  build();"}</p>
                  <p>{"> share();"}</p>
                </div>
              </ParallaxTile>

              <ParallaxTile
                bg="#FF6600"
                rotate={-2}
                delay={0.9}
                parallaxY={yMid}
                className="right-[28%] bottom-[6%] w-[36%] aspect-[5/4] text-white"
                testid="hero-tile-entrepreneur"
              >
                <Lightbulb size={26} strokeWidth={2.5} />
                <p className="mt-3 font-display font-black text-lg leading-tight">
                  Entrepreneurship
                </p>
                <p className="mt-1 font-mono text-[10px] uppercase">
                  Solve real problems
                </p>
              </ParallaxTile>

              <ParallaxTile
                bg="#1A1A1A"
                rotate={2}
                delay={1.1}
                parallaxY={yNear}
                className="left-[4%] bottom-[4%] w-[40%] aspect-[5/4] text-white"
                testid="hero-tile-portfolio"
              >
                <div className="flex items-center justify-between">
                  <FolderHeart size={24} strokeWidth={2.5} />
                  <Sparkles size={16} strokeWidth={2.5} className="text-[#FFD700]" />
                </div>
                <p className="mt-3 font-display font-black text-lg leading-tight">
                  Student portfolios
                </p>
                <div className="mt-2 flex gap-1">
                  <span className="h-2 w-2 rounded-full bg-[#FF007F]" />
                  <span className="h-2 w-2 rounded-full bg-[#FFD700]" />
                  <span className="h-2 w-2 rounded-full bg-[#00C2B8]" />
                </div>
              </ParallaxTile>

              <motion.div
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.7, duration: 0.6 }}
                className="absolute right-[12%] bottom-[36%] animate-spark"
              >
                <Rocket size={32} strokeWidth={2.5} className="text-[#1A1A1A] drop-shadow" />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-5 h-3 border-t-2 border-[#1A1A1A]">
        <div className="bg-[#FFD700]" />
        <div className="bg-[#FF6600]" />
        <div className="bg-[#FF007F]" />
        <div className="bg-[#0055FF]" />
        <div className="bg-[#00C2B8]" />
      </div>
    </section>
  );
}
